/* eslint-disable */
/* @ts-nocheck */
import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { CtxType, fetchRequest, RequestMethod } from '@services/api/utils/utils';
import { userFromBack } from '@features/user/responseFormatters';
import { QuestionnaireAlertResponse, QuestionnaireResponseFront } from '@features/quiz/types';
import { quizFromBack, quizsFromBack, questionnaireResponseToBack } from '@features/quiz/responseFormatters';
import { UserLoginFront, UserRegisterFront, UserUpdateFront } from '@features/user/types';

interface CoopVoucher {
  url: string;
  hash: string;
  cannLToken: string;
  used: boolean;
  assignedAt: Date;
}

interface VoucherUrlResponse {
  url: string;
}

export interface InvitationResponse {
  isExpired: boolean;
}

export const Keys = {
  users: 'users',
  quiz: 'quiz',
  quizs: 'quizs',
  coopVoucher: 'coopVoucher',
  export: 'export',
} as const;

type MutationFunction<TData, TVariables> = (variables: TVariables) => Promise<TData>;

interface UseApiSendOptions<TData, TVariables> extends UseMutationOptions<TData, unknown, TVariables, unknown> {
  key: any[];
}

export const useApiSend = <TData, TVariables>(
  fn: MutationFunction<TData, TVariables>,
  key: any[],
  options: Partial<UseApiSendOptions<TData, TVariables>> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: key,
    mutationFn: fn,
    retry: 1,
    onSuccess: data => {
      key &&
        key.forEach(key => {
          void queryClient.invalidateQueries(key);
        });
    },
    onError: err => {
      console.error('An error occurred during mutation:', err);
    },
    ...options,
  });
};

const useApi = {
  [Keys.users]: {
    register: ({ body }: { body: UserRegisterFront }) => {
      return fetchRequest({
        endpoint: '/users',
        method: RequestMethod.POST,
        body,
        protectedRoute: false,
      });
    },
    login: ({ body }: { body: UserLoginFront }) => {
      return fetchRequest({
        endpoint: '/auth',
        method: RequestMethod.POST,
        body,
        protectedRoute: false,
      });
    },
    requestForgotPassword: ({ body }: { body: { email: string } }) => {
      return fetchRequest({
        endpoint: '/users/reset-password-query',
        method: RequestMethod.POST,
        body,
        protectedRoute: false,
      });
    },
    resetPassword: ({ body }: { body: { password: string; token: string } }) => {
      return fetchRequest({
        endpoint: '/users/reset-password',
        method: RequestMethod.POST,
        body,
        protectedRoute: false,
      });
    },
    me: () => {
      return fetchRequest({
        endpoint: '/users/me',
        method: RequestMethod.GET,
        protectedRoute: true,
        responseFormatter: userFromBack,
      });
    },
    changePassword: ({ body }: { body: { password: string; oldPassword: string } }) => {
      return fetchRequest({
        endpoint: '/users/me/change-password',
        method: RequestMethod.POST,
        body,
        protectedRoute: true,
      });
    },
    updateAccount: ({ body }: { body: UserUpdateFront }) => {
      return fetchRequest({
        endpoint: '/users/me',
        method: RequestMethod.PUT,
        body,
        protectedRoute: true,
      });
    },
    updateArticleRead: ({ body }: { body: { email: string; articleId: string } }) => {
      return fetchRequest({
        endpoint: '/users/article-read',
        method: RequestMethod.POST,
        body,
        protectedRoute: true,
      });
    },
    // send the mail & mark the quiz feedback as done
    finalQuizSend: () => {
      return fetchRequest({
        endpoint: '/users/final-quiz-send',
        method: RequestMethod.POST,
        protectedRoute: true,
      });
    },
    unsubscribe: ({ body }: { body: { email: string } }) => {
      return fetchRequest({
        endpoint: `/users/unsubscribe`,
        method: RequestMethod.POST,
        protectedRoute: false,
        body,
      });
    },
    checkInvitation: ({ token }: { token: string }) => {
      return fetchRequest<InvitationResponse, Error>({
        endpoint: `/users/check-invitation`,
        method: RequestMethod.GET,
        query: { token },
        protectedRoute: false,
      });
    },
  },
  [Keys.quiz]: {
    addResponse: ({ body, method }: { body: QuestionnaireResponseFront; method: 'POST' | 'PUT' }) => {
      return fetchRequest({
        endpoint: method === 'POST' ? '/questionnaireResponses' : `/questionnaireResponses/${body.questionnaireKey}`,
        method: method === 'POST' ? RequestMethod.POST : RequestMethod.PUT,
        protectedRoute: true,
        body: questionnaireResponseToBack(body),
      });
    },
    getQuiz: ({ queryKey }: { queryKey: string }) => {
      return fetchRequest({
        endpoint: `/questionnaireResponses/${queryKey}`,
        method: RequestMethod.GET,
        protectedRoute: true,
        responseFormatter: quizFromBack,
      });
    },
    updateQuizAlert: ({ body, quizId }: { body: QuestionnaireAlertResponse; quizId: string }) => {
      return fetchRequest({
        endpoint: `/questionnaireResponses/${quizId}`,
        method: RequestMethod.PUT,
        protectedRoute: true,
        body: body,
      });
    },
  },
  [Keys.quizs]: {
    getQuizs: () => {
      return fetchRequest({
        endpoint: '/questionnaireResponses',
        method: RequestMethod.GET,
        protectedRoute: true,
        responseFormatter: quizsFromBack,
      });
    },
  },
  [Keys.coopVoucher]: {
    assign: () => {
      return fetchRequest<CoopVoucher, Error>({
        endpoint: '/coop-voucher/assign',
        method: RequestMethod.POST,
        protectedRoute: true,
      });
    },
    getRedirectUrl: ({ params }: { params: { hash: string } }) => {
      return fetchRequest<VoucherUrlResponse, Error>({
        endpoint: '/coop-voucher/:hash/url',
        method: RequestMethod.GET,
        params,
        protectedRoute: false,
      });
    },
  },
  [Keys.export]: {
    approveExport: ({ token }: { token: string }) => {
      return fetchRequest({
        endpoint: `/export/approve`,
        method: RequestMethod.GET,
        query: { token },
        protectedRoute: true,
      });
    },
  },
};

export default useApi;
